import "./Athlete.css";
import type { MouseEvent, ReactNode } from "react";
import { useNavigate } from "react-router";
import { Status, Table, type Column } from "../../kit/index.js";
import { useModel } from "../context.js";
import { bodyOf, bodyText, effortOf, effortWord, levelWord } from "../model/effort.js";
import { liftTarget, setsText } from "../model/plan.js";
import { findExercise, liftLines, liftName, type CoachSession, type LiftLine } from "../model/sessions.js";
import { Icon } from "../parts.js";
import { href } from "../paths.js";
import type { BodyReport, Session } from "../model/types.js";

export function RowLink({ to, onOpen, children }: { to: string; onOpen?: () => void; children: ReactNode }) {
  const navigate = useNavigate();
  const click = (event: MouseEvent) => {
    if ((event.target as Element).closest("a, button, select, summary")) return;
    onOpen?.();
    navigate(to);
  };
  return (
    <tr className="row-link" onClick={click}>
      {children}
    </tr>
  );
}

/** A personal best by its label ("120 kg × 3"), the trophy and the words for VoiceOver. */
export function RecordMark({ label }: { label: string }) {
  return (
    <span className="k-pb">
      {Icon.trophy}
      <span aria-hidden="true">{label}</span>
      <span className="sr">{"personal best: " + label}</span>
    </span>
  );
}

const BODY_ICON: Record<BodyReport["level"], ReactNode> = {
  hurt: (
    <svg viewBox="0 0 12 12" aria-hidden="true">
      <path fill="currentColor" d="M6 .8 11.6 10.8H.4L6 .8Zm-.65 3.6.15 3.4h1l.15-3.4h-1.3Zm.65 4.3a.7.7 0 1 0 0 1.4.7.7 0 0 0 0-1.4Z" />
    </svg>
  ),
  sore: (
    <svg viewBox="0 0 12 12" aria-hidden="true">
      <circle cx="6" cy="6" r="4.8" fill="none" stroke="currentColor" strokeWidth="1.3" />
      <path d="M3.4 6.6c.8-1.2 1.6-1.2 2.6 0s1.8 1.2 2.6 0" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  ),
};

/** Sore or Hurt as an icon and the word: never colour alone. */
export function BodyLevel({ level, text }: { level: BodyReport["level"]; text?: string }) {
  return (
    <span className={"fx-body is-" + level}>
      {BODY_ICON[level]}
      <span>{text ?? levelWord(level)}</span>
    </span>
  );
}

/** How hard a workout was (`RPE 7`) and what was sore or hurt, as the athlete reported it (§27.2). */
export function EffortMarks({ session, note }: { session: Session | null | undefined; note?: boolean }) {
  const effort = effortOf(session);
  const body = bodyOf(session);
  if (effort == null && !body) return null;
  return (
    <span className="fx-marks">
      {effort != null ? (
        <span className="fx-rpe num" title={effort + " · " + effortWord(effort)}>
          <span aria-hidden="true">RPE {effort}</span>
          <span className="sr">{"How hard: " + effort + ", " + effortWord(effort)}</span>
        </span>
      ) : null}
      {body ? <BodyLevel level={body.level} text={bodyText(body)} /> : null}
      {body?.note && note ? <span className="fx-note">“{body.note}”</span> : null}
    </span>
  );
}

function lineResult(line: LiftLine, today: string): ReactNode {
  if (line.waiting) {
    const kind = line.past ? "missed" : line.day === today ? "today" : "planned";
    return <Status kind={kind} text={line.past ? "Not logged" : kind === "today" ? "Today, not done yet" : "Not done yet"} />;
  }
  if (line.skipped) return <Status kind="skipped" />;
  if (!line.value) return <span className="k-muted">Not done · did the session</span>;
  return (
    <span className="num">
      <b>{line.value}</b> <span className="k-muted">top set · {setsText(line.sets ?? 0)}</span>
    </span>
  );
}

/** A session lift by lift: each athlete's top set, set count and personal best, then who has not done it. */
export function LiftSections({ cs, level }: { cs: CoachSession; level: "h3" | "h4" }) {
  const model = useModel();
  const Heading = level;
  const columns: Column<LiftLine>[] = [
    { key: "name", label: "Athlete", render: (l) => l.name },
    {
      key: "result",
      label: "Top set",
      wrap: true,
      render: (l) =>
        l.workout && !l.waiting ? (
          <span className="fx-line">
            {lineResult(l, model.today)}
            <EffortMarks session={l.workout.session} />
          </span>
        ) : (
          lineResult(l, model.today)
        ),
    },
    { key: "record", label: "Personal best", hideLabel: true, align: "end", render: (l) => (l.record ? <RecordMark label={l.record} /> : null) },
  ];
  if (!cs.lifts.length) return <p className="k-muted">No weighed lifts in this session.</p>;
  return (
    <>
      {cs.lifts.map((key) => {
        const found = findExercise(cs, key);
        const target = found ? liftTarget(found.exercise, found.block) : null;
        const cue = found ? [found.exercise.note, found.block.note].find((x) => x) : null;
        const name = liftName(cs, key);
        return (
          <section className="wk-lift" key={key}>
            <Heading className="wk-lift-head">
              {name}
              {target ? <span className="k-muted num"> {target}</span> : null}
            </Heading>
            {cue ? <p className="wk-cue">{cue}</p> : null}
            <Table
              rows={liftLines(cs, key, model.today)}
              columns={columns}
              rowKey={(l) => l.athleteID + (l.workout?.session.id ?? "")}
              href={(l) => href(model.monday, "athlete", l.athleteID)}
              label={name}
              empty="Nobody has this session yet."
            />
          </section>
        );
      })}
    </>
  );
}
